/**
 * A workflow as it is stored (§5.15): the bounds on its canvas and the sanitizer
 * every save goes through. Here rather than in the desktop app because an
 * organization shares its workflows (§5.25), and the account area has to hold
 * them to what the canvas could have drawn.
 */
import { EFFORT_IDS, MODEL_IDS, agentForModel, launchEffort } from '../settings/models';
import { branch } from '../settings/primitives';
import type { AgentId } from '../settings/agents';
import type {
  StepCheck,
  StepCheckScope,
  StepDataFormat,
  StepHeader,
  StepHost,
  StepHostKind,
  StepHttpMethod,
  WorkflowEdge,
  WorkflowInput,
  WorkflowProject,
  WorkflowStep,
  StepWhere
} from './types';

/** How many an install — or an organization — keeps, and how big one gets — bounds on a canvas. */
export const MAX_WORKFLOWS = 20;
export const MAX_WORKFLOW_NAME_CHARS = 60;
export const MAX_STEPS = 30;
export const MAX_WORKFLOW_EDGES = 60;
export const MAX_STEP_NAME_CHARS = 60;
/** An instruction is a paragraph — the task holds the context it is read against. */
export const MAX_INSTRUCTION_CHARS = 4000;
export const MAX_CHECK_CHARS = 300;

const SCOPES: readonly StepCheckScope[] = ['none', 'changed', 'all', 'command'];

/** What a step's check is when nothing has been chosen — the safe reading. */
export const NO_CHECK: StepCheck = { scope: 'none', command: '' };

/** The canvas is unbounded in feel and bounded in fact, as the flow canvas is. */
const MAX_COORD = 100_000;

/** An id minted by the canvas — the same shape every other graph here uses. */
const ID = /^[A-Za-z0-9_-]{1,40}$/;

/* ── a workflow as it is stored ─────────────────────────────────────────── */

/** A step's own target: a name of at most 120 characters, kept only when set. */
export function stepTargetOf(raw: unknown): { target?: string } {
  if (typeof raw !== 'string') return {};
  const name = raw.trim().slice(0, 120);
  return name ? { target: name } : {};
}

/** How many projects one workflow or step names, and how long a name or a branch's template gets. */
export const MAX_WORKFLOW_PROJECTS = 64;
const MAX_PROJECT_NAME_CHARS = 120;
export const MAX_BRANCH_TEMPLATE_CHARS = 300;

/** A branch written as a template: its value is known only when the run reaches it, and is held to `branch()` then. */
export function isBranchTemplate(value: string): boolean {
  return value.includes('{{');
}

/** What is wrong with a branch as written, or null: empty is the session's own, a template is judged when rendered. */
export function branchProblem(value: string): string | null {
  const written = value.trim();
  if (!written) return null;
  if (isBranchTemplate(written)) {
    return written.length > MAX_BRANCH_TEMPLATE_CHARS
      ? `A branch's template holds at most ${MAX_BRANCH_TEMPLATE_CHARS} characters.`
      : null;
  }
  return branch(written) ? null : `“${written.slice(0, 80)}” is not a branch name.`;
}

/** The projects a workflow or a step names, read off anything: a row of no use is dropped, one project once. */
export function workflowProjectsOf(raw: unknown): WorkflowProject[] {
  if (!Array.isArray(raw)) return [];
  const out: WorkflowProject[] = [];
  for (const row of raw.slice(0, MAX_WORKFLOW_PROJECTS) as { project?: unknown; branch?: unknown }[]) {
    const project = typeof row?.project === 'string' ? row.project.trim().slice(0, MAX_PROJECT_NAME_CHARS) : '';
    const written = typeof row?.branch === 'string' ? row.branch.trim() : '';
    if (!project || branchProblem(written) || out.some((one) => one.project === project)) continue;
    out.push({ project, branch: written });
  }
  return out;
}

/** A save's reading of them: a branch git could never hold is said, not dropped. */
export function sanitizeWorkflowProjects(raw: readonly WorkflowProject[]): WorkflowProject[] {
  for (const row of raw) {
    const problem = branchProblem(row.branch ?? '');
    if (problem) throw new Error(`${row.project}: ${problem}`);
  }
  return workflowProjectsOf(raw);
}

/** Where a step runs, kept only when it is one of the two. */
export function stepWhereOf(raw: unknown): { where?: StepWhere } {
  return raw === 'sandbox' || raw === 'worktree' ? { where: raw } : {};
}

export function stepProjectsOf(raw: unknown): { projects?: WorkflowProject[] } {
  const projects = workflowProjectsOf(raw);
  return projects.length ? { projects } : {};
}

/**
 * The graph as it will be written, held to what a run can do. It throws the
 * sentence the user gets back, so the rules live in one place rather than one
 * per surface (`sanitizeFlow`, `sanitizeVariables`).
 *
 * A step with no instruction is kept, not refused: a node dropped on the canvas
 * is empty until it is written, and refusing to save it would lose the shape the
 * user is drawing. A run treats it as an ordinary step with nothing to say.
 */
export function sanitizeWorkflow(input: WorkflowInput): WorkflowInput {
  if (input.steps.length > MAX_STEPS) throw new Error(`A workflow holds at most ${MAX_STEPS} steps.`);
  if (input.edges.length > MAX_WORKFLOW_EDGES)
    throw new Error(`A workflow holds at most ${MAX_WORKFLOW_EDGES} arrows.`);

  const steps: WorkflowStep[] = [];
  for (const step of input.steps) {
    if (!ID.test(step.id)) throw new Error('That canvas sent a step without a usable id.');
    if (steps.some((s) => s.id === step.id)) throw new Error('That canvas sent two steps with the same id.');
    steps.push({
      id: step.id,
      // Capped, never trimmed: the canvas writes while the step is being typed
      // and adopts the row that comes back, so trimming here deletes the space
      // the user just pressed and puts their caret behind it. The run trims what
      // it hands the agent (`buildPrompt`), which is where it matters.
      name: step.name.slice(0, MAX_STEP_NAME_CHARS),
      instruction: step.instruction.slice(0, MAX_INSTRUCTION_CHARS),
      ...stepRunAs(step.model, step.effort),
      conversation: conversationOf(step),
      annotate: step.annotate === true,
      gate: step.gate === 'ask' ? 'ask' : 'auto',
      check: sanitizeCheck(step.check),
      ...hostOf(step.host),
      ...stepTargetOf(step.target),
      ...stepProjectsOf(sanitizeWorkflowProjects(step.projects ?? [])),
      ...stepWhereOf(step.where),
      x: coord(step.x),
      y: coord(step.y)
    });
  }

  const known = new Set(steps.map((s) => s.id));
  const edges: WorkflowEdge[] = [];
  for (const edge of input.edges) {
    if (!ID.test(edge.id)) throw new Error('That canvas sent an arrow without a usable id.');
    // An arrow to a step that is gone is not an arrow: dropped rather than
    // refused, since deleting a step is how that happens. A self-loop is kept —
    // "check, and try again until it passes" is one step pointing at itself, and
    // the visit budget is what stops it.
    if (!known.has(edge.source) || !known.has(edge.target)) continue;
    if (edges.some((e) => e.source === edge.source && e.target === edge.target && e.when === edge.when)) continue;
    if (edges.some((e) => e.id === edge.id)) throw new Error('That canvas sent two arrows with the same id.');
    const when = edgeWhen(edge.when);
    // `each` leaves a foreach or a parallel only, `case` a step that branches only; drawn from anything else either is an ordinary arrow.
    const from = steps.find((one) => one.id === edge.source)?.host?.kind;
    const kept =
      (when === 'each' && !FANS_OUT.has(from)) || (when === 'case' && !BRANCHES.has(from)) ? 'always' : when;
    const value = kept === 'case' && typeof edge.value === 'string' ? edge.value.trim().slice(0, MAX_CASE_CHARS) : null;
    edges.push({ id: edge.id, source: edge.source, target: edge.target, when: kept, ...(kept === 'case' ? { value: value ?? '' } : {}) });
  }

  return { steps, edges };
}

/** A conversation name: lower case, letters, digits and dashes, starting with a letter. */
const CONVERSATION = /^[a-z][a-z0-9-]{0,23}$/;

/**
 * The conversation a step runs in, held to the grammar — `main`, `new`, or a
 * name. A step written before conversations existed reads back from `fresh`.
 * Clamped rather than refused: a name typed with a space or a capital becomes
 * the name it was meant to be, and one that cannot be is the session's own.
 */
export function conversationOf(step: { conversation?: unknown; fresh?: unknown }): string {
  if (typeof step.conversation === 'string') {
    const name = step.conversation.trim().toLowerCase().replace(/\s+/g, '-');
    if (CONVERSATION.test(name)) return name;
    return 'main';
  }
  return step.fresh === true ? 'new' : 'main';
}

/**
 * A check as it will be written. The fields the chosen scope does not use are
 * kept rather than cleared — switching from `command` to `changed` and back is a
 * glance at the other option, not a reason to lose what was typed.
 *
 * `named` is a scope an older build wrote — projects by id, which meant
 * something while a workflow was one workspace's. It reads back as `changed`:
 * still a check, and on whatever the task touched.
 */
export function sanitizeCheck(check: StepCheck): StepCheck {
  const scope = (check.scope as string) === 'named' ? 'changed' : check.scope;
  return {
    scope: SCOPES.includes(scope) ? scope : 'none',
    // Capped, not trimmed — the same field-being-typed as the step's own text.
    command: (check.command ?? '').slice(0, MAX_CHECK_CHARS)
  };
}

/**
 * A model or an effort a step names, held to the grammar (§9.3) — anything else
 * is `''`, i.e. the task's own. Clamped rather than refused, because a value from
 * an older build or a wider CLI is a step saying nothing, not a broken canvas.
 */
function alias(value: unknown, known: string[]): string {
  return typeof value === 'string' && known.includes(value) ? value : '';
}

export function edgeWhen(value: unknown): WorkflowEdge['when'] {
  return value === 'pass' || value === 'fail' || value === 'each' || value === 'case' || value === 'error'
    ? value
    : 'always';
}

/** The kinds whose `each` arrows lead to steps they run themselves: a foreach per item, a parallel per branch. */
export const FANS_OUT: ReadonlySet<StepHostKind | undefined> = new Set<StepHostKind | undefined>(['foreach', 'parallel']);
/** The kinds that report a branch, whose `case` arrows are taken by its value. */
export const BRANCHES: ReadonlySet<StepHostKind | undefined> = new Set<StepHostKind | undefined>([
  'switch',
  'review',
  'choice'
]);

export function coord(value: number): number {
  return Number.isFinite(value) ? Math.round(Math.min(Math.max(value, -MAX_COORD), MAX_COORD)) : 0;
}

/**
 * **Every step names its own model and effort.** There is no "keep the task's":
 * a blank half used to mean that, and it made the task's pair a control that
 * silently governed some steps and not others — invisible in the form, and
 * impossible to state on a card.
 *
 * So a step that arrives without a readable pair is given this one rather than
 * left empty. It is the same pair the presets call `CODING`: the one a step
 * that says nothing about itself is most likely to be, and strong enough that
 * inheriting it is never the reason a chain answered worse.
 */
export const STEP_RUN_AS: { model: string; effort: string; agent: AgentId } = {
  model: 'opus',
  effort: 'xhigh',
  agent: 'claude-code'
};

/**
 * A step's triple, held to the grammar (§9.3) and never left blank. The agent is
 * not stored apart from the model — it is *derived* from it, so a step can never
 * carry a model of one agent and the name of the other, and a `model` that came
 * from an older build resolves both at once (§5.10).
 */
export function stepRunAs(model: unknown, effort: unknown): { model: string; effort: string; agent: string } {
  const named = alias(model, MODEL_IDS) || STEP_RUN_AS.model;
  // A Haiku step stored before Haiku took an effort was written bare: it is the
  // light tier, so it reads as low rather than as the default X-High.
  const missing = named === 'haiku' ? 'low' : STEP_RUN_AS.effort;
  // Resolved against the model that is actually used, so a model taking no
  // effort does not carry a level that does not exist there.
  return {
    model: named,
    effort: launchEffort(named, alias(effort, EFFORT_IDS) || missing) ?? '',
    agent: agentForModel(named)
  };
}

export const MAX_HOST_TARGET_CHARS = 2_000;
export const MAX_HOST_BODY_CHARS = 20_000;
export const MAX_HOST_HEADERS = 20;
/** What a `case` arrow is taken for — one value of a switch. */
export const MAX_CASE_CHARS = 100;
/** A foreach runs at most this many items at once — each an agent beside the session, or a call. */
export const MAX_FOREACH_CONCURRENCY = 8;
const HOST_KINDS: readonly StepHostKind[] = [
  'http',
  'file',
  'command',
  'write',
  'if',
  'foreach',
  'slack',
  'notify',
  'transform',
  'parse',
  'extract',
  'merge',
  'wait',
  'approval',
  'switch',
  'workflow',
  'ticket',
  'github',
  'email',
  'target',
  'task',
  'checks',
  'filter',
  'template',
  'diff',
  'memory',
  'parallel',
  'end',
  'choice',
  'chat',
  'ci',
  'land',
  'review',
  'sync',
  'project',
  'page',
  'tunnel',
  'sql',
  'vote',
  'summarize',
  'structured'
];
/** A choice offers 2 to this many answers. */
export const MAX_CHOICES = 8;
/** A vote asks this many agents at most. */
export const MAX_VOTERS = 5;
/** A filter keeps this many items at most. */
export const MAX_FILTER_LIMIT = 10_000;
export const MAX_HOST_RETRIES = 5;
/** A wait holds a day at most — past that it is a schedule's job. */
export const MAX_WAIT_SECONDS = 86_400;
const METHODS: readonly StepHttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const FORMATS: readonly StepDataFormat[] = ['json', 'md', 'text', 'csv', 'yaml', 'image'];
/** An HTTP token: what a header's name may be. Exported so the step panel marks a line it would drop. */
export const HEADER_NAME = /^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,100}$/;

/**
 * A step run on this computer, held to its shape — or nothing, which is an
 * agent's step as every one written before this existed. Clamped rather than
 * refused, as a check is: a step being typed is not a broken canvas.
 */
export function hostOf(value: unknown): { host?: StepHost } {
  if (!value || typeof value !== 'object') return {};
  const host = value as Partial<Record<keyof StepHost, unknown>>;
  const kind = (HOST_KINDS as readonly unknown[]).includes(host.kind) ? (host.kind as StepHostKind) : null;
  if (!kind) return {};
  const target = typeof host.target === 'string' ? host.target.slice(0, MAX_HOST_TARGET_CHARS) : '';
  const body = typeof host.body === 'string' ? host.body.slice(0, MAX_HOST_BODY_CHARS) : undefined;
  const format = (FORMATS as readonly unknown[]).includes(host.format) ? (host.format as StepDataFormat) : undefined;
  const text = (key: 'subject' | 'site' | 'credential' | 'service'): Partial<StepHost> =>
    typeof host[key] === 'string' && (host[key] as string).trim() ? { [key]: (host[key] as string).slice(0, MAX_HOST_TARGET_CHARS) } : {};
  const one = <T extends string>(key: 'service' | 'action', allowed: readonly T[], fallback: T): T =>
    (allowed as readonly unknown[]).includes(host[key]) ? (host[key] as T) : fallback;
  const n = (
    key: 'seconds' | 'retries' | 'concurrency' | 'limit' | 'count' | 'timeout',
    min: number,
    max: number,
    fallback: number
  ): number => {
    const v = host[key];
    return typeof v === 'number' && Number.isFinite(v) ? Math.min(Math.max(Math.round(v), min), max) : fallback;
  };
  const withBody = body !== undefined ? { body } : {};
  // Any step may give up after a while — none set is none.
  const timeout = typeof host.timeout === 'number' ? { timeout: n('timeout', 1, MAX_WAIT_SECONDS, 60) } : {};
  // A command's folder, when one was chosen — none is the session's folder, else the home.
  const cwd =
    kind === 'command' && typeof host.cwd === 'string' && host.cwd.trim()
      ? { cwd: host.cwd.trim().slice(0, MAX_HOST_TARGET_CHARS) }
      : {};
  const shaped = shapeOf(kind, target, body, format, host, { text, one, n, withBody });
  return { host: { ...shaped, ...timeout, ...cwd } };
}

type Helpers = {
  text: (key: 'subject' | 'site' | 'credential' | 'service') => Partial<StepHost>;
  one: <T extends string>(key: 'service' | 'action', allowed: readonly T[], fallback: T) => T;
  n: (
    key: 'seconds' | 'retries' | 'concurrency' | 'limit' | 'count' | 'timeout',
    min: number,
    max: number,
    fallback: number
  ) => number;
  withBody: { body?: string };
};

/** A kind's own fields, held to their shape. */
function shapeOf(
  kind: StepHostKind,
  target: string,
  body: string | undefined,
  format: StepDataFormat | undefined,
  host: Partial<Record<keyof StepHost, unknown>>,
  { text, one, n, withBody }: Helpers
): StepHost {
  const plain = (key: 'where' | 'sortBy' | 'key'): Partial<StepHost> =>
    typeof host[key] === 'string' && (host[key] as string).trim()
      ? { [key]: (host[key] as string).slice(0, MAX_HOST_TARGET_CHARS) }
      : {};
  switch (kind) {
    case 'http': {
      const method = (METHODS as readonly unknown[]).includes(host.method) ? (host.method as StepHttpMethod) : 'GET';
      const headers = Array.isArray(host.headers) ? host.headers.flatMap(headerOf).slice(0, MAX_HOST_HEADERS) : [];
      const retries = n('retries', 0, MAX_HOST_RETRIES, 0);
      return {
        kind,
        target,
        method,
        ...(headers.length ? { headers } : {}),
        ...(method !== 'GET' && method !== 'DELETE' && body !== undefined ? { body } : {}),
        ...(format === 'json' || format === 'text' || format === 'image' ? { format } : {}),
        ...(retries ? { retries } : {})
      };
    }
    case 'file':
      return { kind, target, ...(format === 'json' || format === 'md' || format === 'text' ? { format } : {}) };
    case 'parse':
      return { kind, target, format: format === 'csv' || format === 'yaml' ? format : 'json' };
    case 'write':
      return {
        kind,
        target,
        body: body ?? '',
        ...(format === 'json' || format === 'md' || format === 'text' ? { format } : {}),
        ...(host.append === true ? { append: true } : {})
      };
    case 'slack':
    case 'notify':
    case 'approval':
      return { kind, target, body: body ?? '' };
    case 'extract':
    case 'merge':
      return { kind, target, ...withBody };
    case 'foreach':
      return { kind, target, concurrency: n('concurrency', 1, MAX_FOREACH_CONCURRENCY, 4) };
    case 'wait':
      return { kind, target, seconds: n('seconds', 1, MAX_WAIT_SECONDS, 60) };
    case 'workflow':
      return { kind, target, action: one('action', ['start', 'queue'], 'start'), ...(host.wait === true ? { wait: true } : {}) };
    case 'ticket':
      return {
        kind,
        target,
        service: one('service', ['jira', 'trello'], 'jira'),
        action: one('action', ['create', 'comment'], 'create'),
        ...text('site'),
        ...text('subject'),
        body: body ?? ''
      };
    case 'github':
      return {
        kind,
        target,
        action: one('action', ['comment', 'pull'], 'comment'),
        ...text('service'),
        ...text('site'),
        ...text('subject'),
        body: body ?? ''
      };
    case 'email':
      return {
        kind,
        target,
        service: one('service', ['resend', 'sendgrid', 'postmark'], 'resend'),
        ...text('credential'),
        ...text('site'),
        ...text('subject'),
        body: body ?? ''
      };
    case 'target':
      return { kind, target, ...text('site'), ...text('service'), body: body ?? '' };
    case 'task':
      return { kind, target, ...text('service'), action: one('action', ['start', 'queue'], 'queue'), body: body ?? '' };
    case 'filter': {
      const limit = typeof host.limit === 'number' ? { limit: n('limit', 1, MAX_FILTER_LIMIT, 100) } : {};
      const order = host.order === 'desc' ? 'desc' : 'asc';
      return { kind, target, ...plain('where'), ...plain('sortBy'), ...(host.sortBy ? { order } : {}), ...limit };
    }
    case 'template':
      return { kind, target, body: body ?? '' };
    case 'diff':
      return { kind, target, ...withBody, ...plain('key') };
    case 'memory':
      return { kind, target, action: one('action', ['get', 'set', 'delete'], 'get'), ...withBody };
    case 'parallel':
      return { kind, target, action: one('action', ['all', 'first'], 'all') };
    case 'end':
      return { kind, target, action: one('action', ['done', 'fail'], 'done') };
    case 'choice': {
      const choices = Array.isArray(host.choices)
        ? [
            ...new Set(
              host.choices
                .filter((one): one is string => typeof one === 'string')
                .map((one) => one.trim().slice(0, MAX_CASE_CHARS))
                .filter(Boolean)
            )
          ].slice(0, MAX_CHOICES)
        : [];
      return { kind, target, body: body ?? '', choices };
    }
    case 'chat':
      return { kind, target, service: one('service', ['teams', 'discord', 'webhook'], 'teams'), body: body ?? '' };
    case 'ci':
      return { kind, target, ...text('service'), seconds: n('seconds', 10, MAX_WAIT_SECONDS, 1800) };
    case 'land':
      return { kind, target, ...text('site'), ...text('subject'), action: one('action', ['merge', 'push', 'pull'], 'merge') };
    case 'review':
    case 'sync':
      return { kind, target };
    case 'project':
      return {
        kind,
        target,
        ...text('service'),
        action: one('action', ['start', 'stop'], 'start'),
        seconds: n('seconds', 0, 600, 60)
      };
    case 'page':
      return { kind, target, action: one('action', ['outline', 'screenshot'], 'outline') };
    case 'tunnel':
      return { kind, target, ...text('service') };
    case 'sql':
      return { kind, target, service: one('service', ['sqlite', 'postgres', 'mysql'], 'sqlite'), ...text('site'), body: body ?? '' };
    case 'vote':
      return { kind, target, body: body ?? '', ...text('subject'), count: n('count', 2, MAX_VOTERS, 3) };
    case 'summarize':
      return { kind, target, ...withBody };
    case 'structured':
      return { kind, target, body: body ?? '', ...text('subject') };
    default:
      return { kind, target };
  }
}

/** A header a server takes: a token for a name, its value kept as a template. */
function headerOf(value: unknown): StepHeader[] {
  if (!value || typeof value !== 'object') return [];
  const { name, value: text } = value as { name?: unknown; value?: unknown };
  if (typeof name !== 'string' || typeof text !== 'string') return [];
  const trimmed = name.trim();
  return HEADER_NAME.test(trimmed) ? [{ name: trimmed, value: text.slice(0, MAX_HOST_TARGET_CHARS) }] : [];
}
